import { DocumentType, IntelliUploadStatus } from "@/core/repositories/intelli-upload";
import { atom } from "jotai";

export interface IntelliUpload {
  key: string;
  documentTypeId: number | null;
  documentType: DocumentType | null;
  documentSubTypeId: number | null;
  documentSubType: string | null;
  status: IntelliUploadStatus;
  error: string | null;
  validationError: string | null;
  file: File;
  filePath: string | null;
  checksumValue: string | null;
  purchaser: string | null;
  purchaserSelected: string | null;
  isPending: boolean;
  isUploading: boolean;
  isUploaded: boolean;
  isFailed: boolean;
  isDeleting: boolean;
}

export interface IntelliUploadProcessed {
  documentId: number;
  documentType: DocumentType;
  documentSubtype: string;
  purchaserName: string;
  fileDisplayName: string;
  fileMimeType: string;
  claimNumber?: string | null;
  claimNumberInput?: string | null;
  checkNumber?: string | null;
  checkNumberInput?: string | null;
  isProcessing: boolean;
  isProcessed: boolean;
  error: string | null;
}

export interface IntelliUploadState {
  documents: IntelliUpload[];
  processedDocuments: IntelliUploadProcessed[];
  queueId: string | null;
  isClearing?: boolean;
  processSubmittedAt?: Date | null;
  isProcessing?: boolean;
  isProcessed?: boolean;
  isRuleEngineInitiating?: boolean;
  isRuleEngineTriggered?: boolean;
}

export const intelliUploadAtom = atom<IntelliUploadState>({
  documents: [],
  processedDocuments: [],
  queueId: null,
  isClearing: false,
  processSubmittedAt: null,
  isProcessing: false,
  isProcessed: false,
  isRuleEngineInitiating: false,
  isRuleEngineTriggered: false,
});

export const getIntelliUploadQueueIdAtom = atom(get => {
  return get(intelliUploadAtom).queueId;
});

export const getIntelliUploadAtom = atom(get => {
  return get(intelliUploadAtom).documents;
});

export const getIntelliUploadProcessedAtom = atom(get => {
  return get(intelliUploadAtom).processedDocuments;
});

export const isUploadingQueueAtom = atom(get => {
  return get(intelliUploadAtom).documents.some(intelliUpload => intelliUpload.isUploading);
});

export const isUploadedQueueAtom = atom(get => {
  return get(intelliUploadAtom).documents.some(intelliUpload => intelliUpload.isUploaded);
});

export const isClearingQueueAtom = atom(get => {
  return get(intelliUploadAtom).isClearing ?? false;
});

export const isProcessingQueueAtom = atom(get => {
  return get(intelliUploadAtom).isProcessing ?? false;
});

export const isProcessedQueueAtom = atom(get => {
  return get(intelliUploadAtom).isProcessed ?? false;
});

export const isRuleEngineTriggeredQueueAtom = atom(get => {
  return get(intelliUploadAtom).isRuleEngineTriggered ?? false;
});

export const isRuleEngineInitiatingQueueAtom = atom(get => {
  return get(intelliUploadAtom).isRuleEngineInitiating ?? false;
});

export const isProcessedDocumentsValidToProceedAtom = atom(get => {
  return get(intelliUploadAtom).processedDocuments.every(doc => {
    if (!doc.isProcessed) {
      return false;
    }
    if (doc.documentType === "Backup" && doc.claimNumber?.startsWith("DUMMY") && !doc.claimNumberInput) {
      return false;
    }
    if (doc.documentType === "Remittance" && doc.checkNumber?.startsWith("DUMMY") && !doc.checkNumberInput) {
      return false;
    }
    return true;
  });
});

export const processSubmittedAtAtom = atom(get => {
  return get(intelliUploadAtom).processSubmittedAt;
});